import React, { useState } from 'react';
import { upsellNavigation } from '../../../analytics';
import BEMHelper from '@ynap/bem-helper';
import { SingleBadge } from '@ynap/badge';
import { PriceWithSchema } from '@ynap/price';
import { ProductInformation, ColoursWithLink, CTAButtons, SizeSelectModal } from '../../../index';
import { useConfig } from '@ynap/immutables-provider';
import { array, bool, func, object, shape, string } from 'prop-types';
import { Notification } from '@ynap/notification';
import ReactMarkdown from 'markdown-to-jsx';

const bem = new BEMHelper('ReducedProductDetails');

const ReducedProductDetails = (props) => {
    const {
        addToCart,
        product,
        messages,
        locale,
        generateListingUrl,
        actionsLoading: { addToCartLoading },
        actionsErrors: errors,
        plpUrlTemplate,
        baseUrl,
        urlConfig,
        config: { head: { pdp: { enableMultipleWishlists, includeCartWithAddToBag } = {} } = {} } = {},
        features = {},
        setModal,
        ogMetaData,
        sizeGridFeatureToggle,
        designerLinkOnClick,
        setUser,
        brandId,
        setReducedMode,
        tracking: { trackedAddToBag, trackedAddToBagError, trackedGoToBag, safeTrackEvent },
        soldOut,
    } = props;
    const {
        name,
        designerName,
        designerSeo,
        colourSwatches,
        skus,
        sizeLabel,
        label: colourLabel,
        selectedSku,
        buyable,
        price,
        badges: { selectedProductColorFirstBadge, selectedProductSizeBadge } = {},
        selectedSize,
        consideredBadge,
        fjwThresholdHandler,
        tracking,
    } = product;

    const [sizeModalOpen, setSizeModalOpen] = useState(false);
    const [sizeSkuModal, setSizeSkuModal] = useState('');
    const isStockBadgesVisible = !!(sizeGridFeatureToggle && selectedProductSizeBadge?.label);
    const showColour = !features?.hideColour;
    const sizeConversionBadge =
        sizeLabel &&
        selectedSize &&
        messages.sizes.sizeConversion({
            sizeLabel: selectedSku?.size?.centralSizeLabel ? selectedSku.size.centralSizeLabel : sizeLabel,
            localLanguageType: selectedSize.name,
            localSizeValue:
                selectedSize.labels.length > 1 ? `${selectedSize.labels[0]}-${selectedSize.labels[selectedSize.labels.length - 1]}` : selectedSize.labels[0],
        });
    const isSizeConversionBadgeVisible = !!(sizeConversionBadge && selectedSize);
    const isProductSizeDisplayable = !!skus?.length;
    const selectedProduct = {
        personalShopperNotification: buyable?.notification?.contactUsCTA,
        eipPersonalShopperNotification: buyable?.notification?.eip?.contactUsCTA,
        name: product.name,
        type: buyable?.notification?.type,
        oneSize: skus && skus.length === 1,
        isWatchExperts: features?.isWatchExperts,
    };
    const config = useConfig();
    const { emailUsCTA = null, callUsCTA = null } = features;

    const modifier = buyable?.canAddToBag || buyable?.canAddToWishlist ? '' : 'hideMargin';

    let showContactUsCTABottom = !emailUsCTA?.Component && buyable?.notification?.type === 'showContactUsCTABottom';

    const showContactUsCTATop =
        !emailUsCTA?.Component && (buyable?.notification?.contactUsCTA || buyable?.notification?.eip?.contactUsCTA) && !showContactUsCTABottom;

    const showCallUsCTA = callUsCTA?.Component && !emailUsCTA?.Component && !showContactUsCTATop;

    const cartButton = addToCart && {
        addToCart: trackedAddToBag,
        failedAddToBag: trackedAddToBagError,
        goToCart: trackedGoToBag,
        addToCartLoading,
    };

    const errorNotificationTimer = config?.head?.errorNotificationTimer;

    const mrpHandler = brandId === 'mrp';
    const napHandler = brandId === 'nap';

    const notificationTypes = {
        shippingRestricted: 'shippingRestricted',
        countryShippingRestricted: 'countryShippingRestricted',
    };

    const renderNotification = (notification, reducedMode) => {
        if (!notification?.message) return;
        let onClick = () => {};

        if (notification?.type === notificationTypes.shippingRestricted) {
            onClick = (e) => {
                e.preventDefault();
                setModal('shippingRestrictions');
            };
        }

        if (notification?.type === notificationTypes.countryShippingRestricted) {
            // If there's a productUrl we want to enable the product link instead of country selection modal
            onClick = !notification.productUrl
                ? (e) => {
                      e.preventDefault();
                      window.SF.triggerChangeCountryFlow();
                  }
                : () => {};
        }

        return (
            <Notification
                type="info"
                className={bem('notification', { reducedMode, soldOut })}
                message={
                    <ReactMarkdown
                        options={{
                            wrapper: React.Fragment,
                            forceWrapper: true,
                            overrides: {
                                a: (props) => (
                                    <a href={props.href} onClick={onClick} target="_blank" rel="noopener noreferrer">
                                        {props.children}
                                    </a>
                                ),
                            },
                        }}
                    >
                        {notification?.message}
                    </ReactMarkdown>
                }
            />
        );
    };

    return (
        <>
            <div className={bem('basicInfo', { soldOut })}>
                <ProductInformation
                    className={{
                        container: bem('productInformation'),
                        designerModifier: 'stickyCta',
                        nameModifier: 'stickyCta',
                        soldOut: soldOut,
                    }}
                    productId={product.colourPartNumber}
                    skuId={selectedSku && selectedSku.partNumber}
                    name={name}
                    designerName={designerName}
                    designerSeo={designerSeo}
                    locale={locale}
                    plpUrlTemplate={plpUrlTemplate}
                    messages={messages}
                    generateListingUrl={generateListingUrl}
                    baseUrl={baseUrl}
                    designerLinkOnClick={designerLinkOnClick}
                />

                <PriceWithSchema
                    offerUrl={ogMetaData?.url}
                    details
                    {...price}
                    className={bem('price', { soldOut })}
                    messages={messages}
                    locale={locale}
                    priceSuffix={messages.price?.taxIncluded && messages.price.taxIncluded()}
                    addMicrodata={true}
                    soldOut={soldOut}
                />

                {selectedProductColorFirstBadge && <SingleBadge className={bem('productColourBadge', { soldOut })} badge={selectedProductColorFirstBadge} />}

                {consideredBadge && (
                    <SingleBadge
                        className={bem('consideredBadge')}
                        considered={true}
                        badge={{ label: consideredBadge, key: 'CONSIDERED_BADGE' }}
                        translation={messages.considered.badge()}
                    />
                )}

                {showColour && (
                    <ColoursWithLink
                        className={bem('colours')}
                        messages={messages}
                        colours={colourSwatches}
                        colourLabel={colourLabel}
                        onClick={(partNumber, position) =>
                            safeTrackEvent(upsellNavigation, partNumber, position, 'view more colours in this product', 'alt colours')
                        }
                    />
                )}
            </div>

            <div className={bem('cta', { soldOut })}>
                {(isStockBadgesVisible || isSizeConversionBadgeVisible) && (
                    <div className={bem('stockSizeConversionBadgesWrapper')}>
                        {isStockBadgesVisible && <SingleBadge className={bem('stockBadges')} badge={selectedProductSizeBadge} hasBackGround={false} />}

                        {isSizeConversionBadgeVisible && (
                            <SingleBadge className={bem('sizeConversion')} badge={{ label: sizeConversionBadge }} hasBackGround={false} />
                        )}
                    </div>
                )}

                {sizeModalOpen && (
                    <SizeSelectModal
                        product={product}
                        closeModal={() => setSizeModalOpen(false)}
                        messages={messages}
                        cart={cartButton}
                        includeCartWithAddToBag={config?.head?.pdp?.includeCartWithAddToBag}
                        selectedProductVariant={product}
                        locale={locale}
                        errors={errors}
                        setSizeSkuModal={(value) => setSizeSkuModal(value)}
                        preventSkuNotification={config?.head?.pdp?.preventSkuNotification}
                    />
                )}

                <CTAButtons
                    setModal={setModal}
                    className={bem('ctaButtons', modifier)}
                    messages={messages}
                    cart={cartButton}
                    skuPartNumber={selectedSku && selectedSku.partNumber}
                    selectedProductVariant={product}
                    isProductSizeDisplayable={isProductSizeDisplayable}
                    locale={locale}
                    errors={errors}
                    urlConfig={urlConfig}
                    multipleWishlistsFeatureToggle={!!enableMultipleWishlists}
                    modifier={modifier}
                    errorNotificationTimer={errorNotificationTimer}
                    includeCartWithAddToBag={includeCartWithAddToBag}
                    features={features}
                    selectedProduct={selectedProduct}
                    setUser={setUser}
                    stickyCTAButton={false}
                    showCallUsCTA={showCallUsCTA}
                    fjwThresholdHandler={fjwThresholdHandler}
                    mrpHandler={mrpHandler}
                    napHandler={napHandler}
                    category={tracking?.primaryCategory?.child?.label}
                    applyMarginLeft={'buttonMargin'}
                    setReducedMode={setReducedMode}
                    reducedMode={true}
                    setSizeModalOpen={(value) => setSizeModalOpen(value)}
                    sizeSkuModal={sizeSkuModal}
                />
                <div className={bem('wrapperNotification')}>{renderNotification(buyable?.notification, true)}</div>
            </div>
        </>
    );
};

export default ReducedProductDetails;

ReducedProductDetails.propTypes = {
    ogMetaData: shape({
        url: string,
    }),
    product: object.isRequired,
    messages: object.isRequired,
    changeSelectedSku: func.isRequired,
    addToCart: func.isRequired,
    addToWishlist: func.isRequired,
    fetchWishlists: func.isRequired,
    wishlists: array,
    locale: string.isRequired,
    actionsLoading: shape({
        addToCartLoading: bool,
        addToWishlistLoading: bool,
        fetchWishlistsLoading: bool,
    }),
    actionsErrors: object,
    baseUrl: string,
    plpUrlTemplate: string.isRequired,
    generateListingUrl: func.isRequired,
    urlConfig: object,
    internal_urls: array,
    customerCareUrl: string,
    sizeLabelToggle: bool,
    config: shape({
        head: shape({
            pdp: shape({
                enableMultipleWishlists: bool,
                includeCartWithAddToBag: bool,
            }),
        }),
    }),
    features: shape({
        hideSizeSelect: bool,
        accordionTitle: shape({ Component: func }),
    }),
    sizeAndFitInformation: object,
    measurements: object,
    activeEditorialSection: string,
    toggle: bool,
    setSizeChartModal: func,
    tracking: shape({
        trackedAddToBag: func,
        trackedAddToBagError: func,
        trackedGoToBag: func,
        trackedAddToWishlist: func,
        trackedAddToWishlistError: func,
        safeTrackEvent: func,
        trackedAccordionLinkClick: func,
    }),
    setModal: func,
    onDropDown: func,
    sizeGridFeatureToggle: bool,
    designerLinkOnClick: func,
    language: string,
    redirectToWishlist: func,
    isEnabled: bool,
    pdpUrlTemplate: string,
    setUser: func,
    brandId: string,
    setReducedMode: func,
};
